import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageComments from '../components/PageComments';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { calendarStyles, pagePromptThemes, tileFormats, todayPagePrompt } from '../data/pagePrompts';
import { buildProblemSourceDraft, problemDraftStorageKey } from '../data/problems';
import { visibilityOptions } from '../data/widgets';
import { ensureDefaultPrivacyGroups, listPrivacyGroups } from '../services/categoryService';
import { listFriends } from '../services/friendService';
import { getUserProfile } from '../services/profileService';
import {
  createPageEntry,
  deletePageEntry,
  listOwnerPageEntries,
  listVisiblePageEntries,
  updatePageEntry,
} from '../services/contentService';

const emptyEntry = {
  id: '',
  prompt: '',
  theme: 'memory',
  answer: '',
  pageName: 'Default',
  imageUrl: '',
  visibility: 'private',
  visibilityGroupId: '',
  showOnProfile: false,
  tileFormat: 'square',
  entryDate: new Date().toISOString().slice(0, 10),
};

function draftFromStorage() {
  try {
    const draft = JSON.parse(localStorage.getItem('cyberautography:pageDraft') || 'null');
    localStorage.removeItem('cyberautography:pageDraft');
    return draft ? { ...emptyEntry, ...draft } : null;
  } catch (_err) {
    return null;
  }
}

function Pages() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [friendEntries, setFriendEntries] = useState([]);
  const [privacyGroups, setPrivacyGroups] = useState([]);
  const [form, setForm] = useState(() => draftFromStorage() || { ...emptyEntry, ...todayPagePrompt() });
  const [activePage, setActivePage] = useState('Default');
  const [filter, setFilter] = useState('all');
  const [calendarStyle, setCalendarStyle] = useState('tiles');
  const [showComposer, setShowComposer] = useState(() => Boolean(form.answer));
  const [showNewPage, setShowNewPage] = useState(false);
  const [showLayout, setShowLayout] = useState(false);
  const [settingsEntryId, setSettingsEntryId] = useState('');
  const [newPageName, setNewPageName] = useState('');
  const [newPagePrivacy, setNewPagePrivacy] = useState('private');
  const [newPageGroupId, setNewPageGroupId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadPages = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const [ownerEntries, friends] = await Promise.all([
        listOwnerPageEntries(currentUser.uid),
        listFriends(currentUser.uid),
      ]);
      await ensureDefaultPrivacyGroups(currentUser.uid);
      setPrivacyGroups(await listPrivacyGroups(currentUser.uid));
      const visibleFriendEntries = await Promise.all(friends.map(async (friend, index) => {
        let profile = null;
        try {
          profile = await getUserProfile(friend.friendUid);
        } catch (_err) {
          profile = null;
        }
        const visible = await listVisiblePageEntries(friend.friendUid, currentUser.uid);
        return visible.map((entry) => ({
          ...entry,
          friendName: profile?.displayName || friend.friendUid,
          friendColor: `hsl(${(index * 67) % 360} 58% 45%)`,
        }));
      }));
      setEntries(ownerEntries);
      setFriendEntries(visibleFriendEntries.flat());
      if (ownerEntries.length > 0 && !ownerEntries.some((entry) => (entry.pageName || 'Default') === activePage)) {
        setActivePage(ownerEntries[0].pageName || 'Default');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activePage, currentUser]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function editEntry(entry) {
    setForm({ ...emptyEntry, ...entry });
    setShowComposer(true);
    setActivePage(entry.pageName || 'Default');
    setMessage('Editing page.');
  }

  function startNewTile() {
    setForm({ ...emptyEntry, ...todayPagePrompt(), pageName: activePage });
    setShowComposer(true);
  }

  function createLocalPage(event) {
    event.preventDefault();
    const name = newPageName.trim() || 'Untitled page';
    setActivePage(name);
    setForm({ ...emptyEntry, ...todayPagePrompt(), pageName: name, visibility: newPagePrivacy, visibilityGroupId: newPagePrivacy === 'group' ? newPageGroupId : '' });
    setShowComposer(true);
    setShowNewPage(false);
    setNewPageName('');
    setNewPageGroupId('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (!form.prompt.trim() || !form.answer.trim()) {
        throw new Error('Prompt and answer are required.');
      }
      if (form.id) {
        await updatePageEntry(form.id, form);
        setMessage('Page updated.');
      } else {
        await createPageEntry(currentUser.uid, { ...form, pageName: activePage });
        setMessage('Page posted.');
      }
      setForm({ ...emptyEntry, ...todayPagePrompt(), pageName: activePage });
      setShowComposer(false);
      await loadPages();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(entryId) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await deletePageEntry(entryId);
      setMessage('Page deleted.');
      if (form.id === entryId) setForm({ ...emptyEntry, ...todayPagePrompt(), pageName: activePage });
      await loadPages();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function createProblemFromEntry(entry) {
    localStorage.setItem(problemDraftStorageKey, JSON.stringify(buildProblemSourceDraft({
      sourceType: 'pageEntry',
      sourceId: entry.id,
      sourceTitle: entry.prompt,
      sourceText: entry.answer,
    })));
    navigate('/dashboard/problems');
  }

  const pageNames = Array.from(new Set(['Default', ...entries.map((entry) => entry.pageName || 'Default')]));
  const ownActiveEntries = entries.filter((entry) => (entry.pageName || 'Default') === activePage);
  const baseEntries = filter === 'friends' ? friendEntries : filter === 'mine' ? ownActiveEntries : [...ownActiveEntries, ...friendEntries];
  const visibleEntries = baseEntries.filter((entry) => entry.friendName || (entry.pageName || 'Default') === activePage);

  return (
    <>
      <SEO title="Pages" />
      <section className="page-heading">
        <p className="eyebrow">Pages</p>
        <h1>Answer small prompts, inspire the future you, your family, anyone.</h1>
        <p>One featured prompt changes daily. You can answer more, attach a picture link, and decide what belongs on your profile.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="surface-card">
        <div className="page-tab-row">
          {pageNames.map((name) => <button className={`tab-button${activePage === name ? ' active' : ''}`} type="button" key={name} onClick={() => setActivePage(name)}>{name}</button>)}
          <button className="button button-secondary" type="button" onClick={() => setShowNewPage(true)}>New page</button>
          <button className="button button-secondary" type="button" onClick={() => setShowLayout((current) => !current)}>Layout</button>
        </div>
        {showNewPage ? (
          <form className="inline-panel stacked-form" onSubmit={createLocalPage}>
            <h2>New page</h2>
            <label>Name<input value={newPageName} onChange={(event) => setNewPageName(event.target.value)} /></label>
            <label>Default privacy
              <select value={newPagePrivacy} onChange={(event) => setNewPagePrivacy(event.target.value)}>
                {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
            </label>
            {newPagePrivacy === 'group' ? (
              <label>Group
                <select value={newPageGroupId} onChange={(event) => setNewPageGroupId(event.target.value)} required>
                  <option value="">Select group</option>
                  {privacyGroups.map((group) => <option value={group.id} key={group.id}>{group.name}</option>)}
                </select>
              </label>
            ) : null}
            <button className="button" type="submit">Create tab</button>
          </form>
        ) : null}
        {showLayout ? (
          <div className="inline-panel stacked-form">
            <h2>Layout</h2>
            <label>Calendar style
              <select value={calendarStyle} onChange={(event) => setCalendarStyle(event.target.value)}>
                {calendarStyles.map((style) => <option value={style} key={style}>{style}</option>)}
              </select>
            </label>
            <label>Filter
              <select value={filter} onChange={(event) => setFilter(event.target.value)}>
                <option value="all">Mine + friends</option>
                <option value="mine">Mine</option>
                <option value="friends">Friends</option>
              </select>
            </label>
          </div>
        ) : null}
        <div>
          <div className="section-toolbar">
            <div>
              <h2>{activePage}</h2>
              <p className="muted">Post answers as tiles. Settings stay behind each tile menu.</p>
            </div>
            <button className="button" type="button" onClick={startNewTile}>New tile</button>
          </div>
          {loading ? <p>Loading pages...</p> : null}
          {!loading && visibleEntries.length === 0 && !showComposer ? <div className="empty-state"><h3>Empty page</h3><p>This tab is ready. Add your first tile when you have an answer worth keeping.</p></div> : null}
          <div className={`page-tile-grid page-style-${calendarStyle}`}>
            {showComposer ? (
              <form className="page-tile page-tile-wide stacked-form" onSubmit={handleSubmit}>
                <h3>{form.id ? 'Edit tile' : 'New tile'}</h3>
                <label>Theme
                  <select value={form.theme} onChange={(event) => updateForm('theme', event.target.value)}>
                    {pagePromptThemes.map((theme) => <option value={theme} key={theme}>{theme}</option>)}
                  </select>
                </label>
                <label>Prompt<textarea rows="3" value={form.prompt} onChange={(event) => updateForm('prompt', event.target.value)} /></label>
                <label>Answer<textarea rows="7" value={form.answer} onChange={(event) => updateForm('answer', event.target.value)} /></label>
                <label>Visibility
                  <select value={form.visibility} onChange={(event) => updateForm('visibility', event.target.value)}>
                    {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                  </select>
                </label>
                {form.visibility === 'group' ? (
                  <label>Group
                    <select value={form.visibilityGroupId || ''} onChange={(event) => updateForm('visibilityGroupId', event.target.value)} required>
                      <option value="">Select group</option>
                      {privacyGroups.map((group) => <option value={group.id} key={group.id}>{group.name}</option>)}
                    </select>
                  </label>
                ) : null}
                <div className="button-row compact-row">
                  <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Post tile'}</button>
                  <button className="button button-secondary" type="button" onClick={() => setShowComposer(false)}>Cancel</button>
                </div>
              </form>
            ) : null}
            {visibleEntries.map((entry) => (
              <article className={`page-tile page-tile-${entry.tileFormat || 'square'}`} key={`${entry.ownerId}-${entry.id}`} style={{ '--friend-color': entry.friendColor || 'var(--accent)' }}>
                <div className="tile-action-row">
                  <p className="tag">{entry.theme} · {entry.entryDate}</p>
                  <button className="tile-arrow-button" type="button" title="Open a problem from this tile" onClick={() => createProblemFromEntry(entry)}>-&gt;</button>
                </div>
                {entry.friendName ? <p className="friend-label"><span />{entry.friendName}</p> : null}
                <h3>{entry.prompt}</h3>
                {entry.imageUrl ? <img src={entry.imageUrl} alt="Page attachment" /> : null}
                <p>{entry.answer}</p>
                {entry.visibility !== 'private' ? <PageComments entry={entry} canComment={entry.visibility !== 'private'} /> : null}
                {!entry.friendName ? (
                  <>
                    <div className="button-row compact-row">
                      <button className="button button-secondary" type="button" onClick={() => editEntry(entry)}>Edit</button>
                      <button className="button button-secondary" type="button" onClick={() => setSettingsEntryId(settingsEntryId === entry.id ? '' : entry.id)}>...</button>
                    </div>
                    {settingsEntryId === entry.id ? (
                      <div className="inline-panel stacked-form">
                        <label>Visibility
                          <select value={entry.id === form.id ? form.visibility : entry.visibility} onChange={(event) => editEntry({ ...entry, visibility: event.target.value })}>
                            {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                          </select>
                        </label>
                        {(entry.id === form.id ? form.visibility : entry.visibility) === 'group' ? (
                          <label>Group
                            <select value={entry.id === form.id ? form.visibilityGroupId || '' : entry.visibilityGroupId || ''} onChange={(event) => editEntry({ ...entry, visibilityGroupId: event.target.value })}>
                              <option value="">Select group</option>
                              {privacyGroups.map((group) => <option value={group.id} key={group.id}>{group.name}</option>)}
                            </select>
                          </label>
                        ) : null}
                        <label>Tile format
                          <select value={entry.id === form.id ? form.tileFormat : entry.tileFormat} onChange={(event) => editEntry({ ...entry, tileFormat: event.target.value })}>
                            {tileFormats.map((format) => <option value={format} key={format}>{format}</option>)}
                          </select>
                        </label>
                        <label>Picture URL<input value={entry.id === form.id ? form.imageUrl : entry.imageUrl || ''} onChange={(event) => editEntry({ ...entry, imageUrl: event.target.value })} /></label>
                        <label className="checkbox-label"><input type="checkbox" checked={entry.id === form.id ? form.showOnProfile : Boolean(entry.showOnProfile)} onChange={(event) => editEntry({ ...entry, showOnProfile: event.target.checked })} /> Show on profile</label>
                        <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleDelete(entry.id)}>Delete</button>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Pages;
